/**
 * PDP pack-size tiles.
 *
 * The real WooCommerce <select> stays in the DOM because `add-to-cart-variation.js`
 * reads it, rewrites its <option> list and submits it with the form. The tiles are a
 * presentation layer mirrored in both directions:
 *
 *   tile clicked          -> select.value + change  -> WooCommerce resolves the variation
 *   WooCommerce rewrote   -> `woocommerce_update_variation_values` -> tiles re-sync
 *
 * WooCommerce fires its variation events through jQuery, which does not emit native
 * DOM events, so we bind through jQuery when it is present and fall back to native
 * listeners otherwise.
 */

const SELECTED_CLASS = "is-selected";
const DISABLED_CLASS = "is-disabled";

/**
 * Push the state of the native <select> onto the tiles: which one is active,
 * which combinations WooCommerce has ruled out, which options no longer exist.
 *
 * @param {HTMLElement} root   The `.c-variation-tiles` fieldset.
 * @param {HTMLSelectElement} select The attribute select it wraps.
 */
function syncFromSelect(root, select) {
  const value = select.value;

  root.querySelectorAll(".c-variation-tiles__input").forEach((input) => {
    const tile = input.closest(".c-variation-tiles__tile");
    if (!tile) return;

    const option = Array.from(select.options).find((o) => o.value === input.value);

    // WooCommerce removes options that cannot apply to the other chosen attributes.
    if (!option) {
      tile.hidden = true;
      input.disabled = true;
      tile.classList.remove(SELECTED_CLASS);
      return;
    }

    tile.hidden = false;
    input.disabled = option.disabled;
    tile.classList.toggle(DISABLED_CLASS, option.disabled);

    const isSelected = value !== "" && option.value === value;
    input.checked = isSelected;
    tile.classList.toggle(SELECTED_CLASS, isSelected);
  });
}

/**
 * @param {HTMLElement} root The `.c-variation-tiles` fieldset.
 */
function initTiles(root) {
  const select = root.querySelector(".c-variation-tiles__native select");
  if (!select || root.dataset.labifyTiles === "on") return;

  root.dataset.labifyTiles = "on";

  const $ = window.jQuery;
  const form = root.closest("form.variations_form");
  const sync = () => syncFromSelect(root, select);

  root.addEventListener("change", (event) => {
    const input = event.target;
    if (!input.classList || !input.classList.contains("c-variation-tiles__input")) return;

    select.value = input.value;

    // jQuery's trigger reaches WooCommerce's delegated handler; the native
    // dispatch covers the (unlikely) case of jQuery being absent.
    if ($) {
      $(select).trigger("change");
    } else {
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  select.addEventListener("change", sync);

  if ($) {
    $(select).on("change", sync);

    if (form) {
      // `woocommerce_update_variation_values` fires after WooCommerce has rebuilt
      // the option list; `reset_data` after the selection was cleared.
      $(form).on("woocommerce_update_variation_values reset_data", sync);
    }
  }

  initCollapse(root);

  root.classList.add("is-enhanced");
  sync();
}

/**
 * Long option lists (`data-collapse-after="2"`): show the first N tiles and a
 * "Pokaż więcej" button that reveals the rest. A selected tile beyond N stays
 * visible (CSS: `.is-extra:not(.is-selected)`), so the choice is never hidden.
 *
 * @param {HTMLElement} root The `.c-variation-tiles` fieldset.
 */
function initCollapse(root) {
  const after = parseInt(root.dataset.collapseAfter || "0", 10);
  const more = root.querySelector(".c-variation-tiles__more");
  if (!after || !more) return;

  root.querySelectorAll(".c-variation-tiles__tile").forEach((tile, index) => {
    tile.classList.toggle("is-extra", index >= after);
  });

  root.classList.add("is-collapsed");
  more.hidden = false;
  more.setAttribute("aria-expanded", "false");

  more.addEventListener("click", () => {
    root.classList.remove("is-collapsed");
    more.hidden = true;
    more.setAttribute("aria-expanded", "true");
  });
}

function init() {
  document.querySelectorAll(".c-variation-tiles").forEach(initTiles);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
